/**
 * Utilitaires pour créer un FormData pour la création de cours
 * 
 * Supporte deux options :
 * 1. Avec upload de fichiers (multipart/form-data) - recommandé
 * 2. Avec URLs (application/json)
 */

/**
 * Crée un FormData pour créer un cours avec upload de fichiers
 * 
 * @param {Object} courseData - Données du cours
 * @param {File|Blob} courseData.thumbnailFile - Fichier thumbnail (optionnel)
 * @param {Object} courseData - Toutes les autres propriétés du cours
 * @returns {FormData} FormData prêt à être envoyé
 */
export function createCourseFormData(courseData) {
  const formData = new FormData()

  // Champs obligatoires
  if (courseData.title) {
    formData.append('title', courseData.title)
  }
  if (courseData.description) {
    formData.append('description', courseData.description)
  }
  if (courseData.price !== undefined) {
    // Convertir en centimes si nécessaire
    const price = typeof courseData.price === 'number' 
      ? courseData.price 
      : parseInt(courseData.price) || 0
    formData.append('price', price.toString())
  }
  if (courseData.creatorId) {
    formData.append('creatorId', courseData.creatorId)
  }
  if (courseData.categoryId) {
    formData.append('categoryId', courseData.categoryId)
  }

  // Champs optionnels populaires
  if (courseData.shortDescription) {
    formData.append('shortDescription', courseData.shortDescription)
  }

  // Fichier thumbnail
  if (courseData.thumbnailFile instanceof File || courseData.thumbnailFile instanceof Blob) {
    formData.append('thumbnail', courseData.thumbnailFile)
  } else if (courseData.thumbnail) {
    // Si c'est une URL, l'ajouter comme string
    formData.append('thumbnail', courseData.thumbnail)
  }

  // Autres champs optionnels
  if (courseData.originalPrice !== undefined) {
    const originalPrice = typeof courseData.originalPrice === 'number'
      ? courseData.originalPrice
      : parseInt(courseData.originalPrice) || 0
    formData.append('originalPrice', originalPrice.toString())
  }

  if (courseData.currency) {
    formData.append('currency', courseData.currency)
  }

  if (courseData.duration !== undefined) {
    formData.append('duration', courseData.duration.toString())
  }

  if (courseData.level) {
    formData.append('level', courseData.level)
  }

  if (courseData.language) {
    formData.append('language', courseData.language)
  }

  if (courseData.status) {
    formData.append('status', courseData.status)
  }

  if (courseData.isPublished !== undefined) {
    formData.append('isPublished', courseData.isPublished.toString())
  }

  if (courseData.isFeatured !== undefined) {
    formData.append('isFeatured', courseData.isFeatured.toString())
  }

  if (courseData.isFree !== undefined) {
    formData.append('isFree', courseData.isFree.toString())
  }

  if (courseData.maxStudents !== undefined) {
    formData.append('maxStudents', courseData.maxStudents.toString())
  }

  if (courseData.certificate !== undefined) {
    formData.append('certificate', courseData.certificate.toString())
  }

  if (courseData.accessDurationDays !== undefined) {
    formData.append('accessDurationDays', courseData.accessDurationDays.toString())
  }

  // Arrays - envoyer chaque élément individuellement avec la même clé
  // Le backend recevra automatiquement un array quand plusieurs valeurs ont la même clé
  const requirements = courseData.requirements && Array.isArray(courseData.requirements) ? courseData.requirements : []
  requirements.forEach((req) => {
    formData.append('requirements', req)
  })

  const objectives = courseData.objectives && Array.isArray(courseData.objectives) ? courseData.objectives : []
  objectives.forEach((obj) => {
    formData.append('objectives', obj)
  })

  const tagIds = courseData.tagIds && Array.isArray(courseData.tagIds) ? courseData.tagIds : []
  tagIds.forEach((tagId) => {
    formData.append('tagIds', tagId)
  })

  // Meta tags
  if (courseData.metaTitle) {
    formData.append('metaTitle', courseData.metaTitle)
  }

  if (courseData.metaDescription) {
    formData.append('metaDescription', courseData.metaDescription)
  }

  const keywords = courseData.keywords && Array.isArray(courseData.keywords) ? courseData.keywords : []
  keywords.forEach((keyword) => {
    formData.append('keywords', keyword)
  })

  // Slug
  if (courseData.slug) {
    formData.append('slug', courseData.slug)
  }

  return formData
}

/**
 * Détermine si on doit utiliser FormData ou JSON
 * 
 * @param {Object} courseData - Données du cours
 * @returns {boolean} true si FormData doit être utilisé
 */
export function shouldUseFormData(courseData) {
  // Utiliser FormData si on a des fichiers
  return (
    courseData.thumbnailFile instanceof File ||
    courseData.thumbnailFile instanceof Blob
  )
}

/**
 * Prépare les données pour créer un cours
 * Supporte les deux options : FormData (avec fichiers) ou JSON (avec URLs)
 * 
 * @param {Object} courseData - Données du cours
 * @returns {FormData|Object} FormData si fichiers présents, sinon objet JSON
 */
export function prepareCourseData(courseData) {
  if (shouldUseFormData(courseData)) {
    return createCourseFormData(courseData)
  }

  // Option JSON avec URLs
  const jsonData = {
    title: courseData.title,
    description: courseData.description,
    price: typeof courseData.price === 'number' ? courseData.price : parseInt(courseData.price) || 0,
    creatorId: courseData.creatorId,
    categoryId: courseData.categoryId
  }

  // Ajouter les champs optionnels seulement s'ils existent
  if (courseData.shortDescription) jsonData.shortDescription = courseData.shortDescription
  if (courseData.thumbnail) jsonData.thumbnail = courseData.thumbnail
  if (courseData.originalPrice !== undefined) {
    jsonData.originalPrice = typeof courseData.originalPrice === 'number' 
      ? courseData.originalPrice 
      : parseInt(courseData.originalPrice) || 0
  }
  if (courseData.currency) jsonData.currency = courseData.currency
  if (courseData.duration !== undefined) jsonData.duration = courseData.duration
  if (courseData.level) jsonData.level = courseData.level
  if (courseData.language) jsonData.language = courseData.language
  if (courseData.status) jsonData.status = courseData.status
  if (courseData.isPublished !== undefined) jsonData.isPublished = courseData.isPublished
  if (courseData.isFeatured !== undefined) jsonData.isFeatured = courseData.isFeatured
  if (courseData.isFree !== undefined) jsonData.isFree = courseData.isFree
  if (courseData.maxStudents !== undefined) jsonData.maxStudents = courseData.maxStudents
  if (courseData.requirements && Array.isArray(courseData.requirements)) {
    jsonData.requirements = courseData.requirements
  }
  if (courseData.objectives && Array.isArray(courseData.objectives)) {
    jsonData.objectives = courseData.objectives
  }
  if (courseData.certificate !== undefined) jsonData.certificate = courseData.certificate
  if (courseData.accessDurationDays !== undefined) jsonData.accessDurationDays = courseData.accessDurationDays
  if (courseData.tagIds && Array.isArray(courseData.tagIds)) jsonData.tagIds = courseData.tagIds
  if (courseData.metaTitle) jsonData.metaTitle = courseData.metaTitle
  if (courseData.metaDescription) jsonData.metaDescription = courseData.metaDescription
  if (courseData.keywords && Array.isArray(courseData.keywords)) jsonData.keywords = courseData.keywords

  return jsonData
}

